import { useState, useMemo, useEffect } from "react";
import { Filter, Search } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../../../styles/__labOrderManagement.module.scss";
import user from "../../../assets/images/default_avatar.png";
import { toast } from "../../../components/toast";
import { APP_PATH } from "../../../api/backendPath";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchDoctorAppointmentsReducer } from "../../../reducer/appointments/fetchDoctorAppointmentsReducer";
import { joinDoctorAppointmentReducer } from "../../../reducer/joinDoctorAppointment/joinDoctorAppointmentReducer";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import PhysioInvoiceModal from "../../../components/physio-invoice-modal/PhysioInvoiceModal";
import { FiDownload, FiPrinter } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import PhysioDashboardLayout from "../../../components/physio-dashboard-layout/PhysioDashboardLayout";
import PhysioDialogRevisit from "../../../components/physio-dialog-Revisit/PhysioDialogRevisit";
import Chat from "../../../components/chat/ChatModal";
import ChatIconWithBadge from "../../../components/chat/ChatIconWithBadge";
import SlaTimer from "../../../components/sla-timer/SlaTimer";
import {
  BookingPatientNameCell,
  BookingChildNameCell,
} from "../../../components/booking-patient-cells/BookingPatientCells";
import { composeBookingChildName } from "../../../utils/childBooking";
import useChatUnread from "../../../hooks/useChatUnread";
import { formatDateNumeric as tzDateNumeric } from "../../../utils/datetime";
import { formatWholeAmount } from "../../../utils/amountFormat";

const PhysioOrderManagement = ({ onLogout, onNavigateToDashboard }) => {
  const { t, i18n } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("Received");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Received");
  const [dateFilter, setDateFilter] = useState("");
  // Location filter (replaces the old status dropdown): "", HOME_VISIT, ON_SITE
  const [locationFilter, setLocationFilter] = useState("");
  // Search typeahead dropdown state
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  // Invoice (Print) modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceOrderId, setInvoiceOrderId] = useState(null);
  // Kept for the table's operating-hours (time-slot) clipping — see getCenterHoursForDate.
  const [centerProfile, setCenterProfile] = useState(null);
  const navigate = useNavigate();
  const PRINTABLE_STATUSES = ["ORDER_ACCEPTED", "THERAPIST_ASSIGNED", "THERAPIST_DISPATCHED", "IN_PROGRESS", "COMPLETED"];
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);
  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openPhysioDialogRevisit, setOpenPhysioDialogRevisit] =
    useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatTarget, setChatTarget] = useState(null);
  // Front-face service-name tooltip. Rendered ONCE as a position:fixed element
  // so it is never clipped by the table's horizontal scroll box, while the
  // table still scrolls horizontally on reduced screens. `svcTip` holds the
  // list of service names plus the {x, y} anchor computed from the hovered cell.
  const [svcTip, setSvcTip] = useState(null);
  const { unreadForAppointment, refresh: refreshChatUnread, clearAppointment } = useChatUnread();

  const openChat = (item) => {
    setChatTarget(item);
    setIsChatOpen(true);
    clearAppointment(item?.bookingId ?? item?.rawId ?? item?.raw?.id);
  };

  const closeChat = () => {
    setIsChatOpen(false);
    setChatTarget(null);
    refreshChatUnread();
  };

  const dispatch = useDispatch();
  const {
    data: apiAppointments,
    isLoading: loading,
    error,
    totalPages: reduxTotalPages,
  } = useSelector((state) => state.fetchDoctorAppointments);

  // Use Redux state for totalPages, fallback to local state if needed
  const totalPages = reduxTotalPages || 1;
  const today = new Date();
  const formatted = format(today, "MMMM dd, yyyy");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  // Static placeholder appointments (kept for reference)

  // Map API orders to table format (when backend returns real data)
  const apiOrdersArray = Array.isArray(apiAppointments) ? apiAppointments : (apiAppointments?.orders || apiAppointments?.data?.orders || []);

  // Preferred-time-slot period bands (minutes since midnight), matching the
  // appointment-detail buckets. The range shown in the table is the band
  // clipped to the center's operating hours for the order's preferred date.
  const PERIOD_BANDS = {
    MORNING: { start: 0, end: 12 * 60 },
    AFTERNOON: { start: 12 * 60, end: 17 * 60 },
    EVENING: { start: 17 * 60, end: 24 * 60 },
  };
  const WEEK_DAY_CODES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const hhmmToMin = (v) => {
    if (!v) return NaN;
    const [h, m] = String(v).split(":").map(Number);
    if (isNaN(h)) return NaN;
    return h * 60 + (isNaN(m) ? 0 : m);
  };
  const minsToLabel12 = (mins) => {
    const norm = ((mins % 1440) + 1440) % 1440;
    const h24 = Math.floor(norm / 60);
    const m = norm % 60;
    const period = h24 >= 12 ? "PM" : "AM";
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
  };
  // Resolve the center's operating hours for the weekday of a given date
  // (prefers the per-day `operating_schedule`, falls back to legacy fields).
  const getCenterHoursForDate = (dateStr) => {
    if (!centerProfile || !dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const dayCode = WEEK_DAY_CODES[d.getDay()];
    const schedule = Array.isArray(centerProfile.operating_schedule)
      ? centerProfile.operating_schedule
      : null;
    if (schedule) {
      const match = schedule.find((s) => s.day === dayCode);
      if (match) {
        return match.is_open
          ? { is_open: true, start: String(match.start || "").slice(0, 5), end: String(match.end || "").slice(0, 5) }
          : { is_open: false };
      }
    }
    const days =
      Array.isArray(centerProfile.operating_days) && centerProfile.operating_days.length
        ? centerProfile.operating_days
        : WEEK_DAY_CODES;
    return {
      is_open: days.includes(dayCode),
      start: String(centerProfile.operating_hours_start || "08:00").slice(0, 5),
      end: String(centerProfile.operating_hours_end || "17:00").slice(0, 5),
    };
  };
  // Format "<Period> (<range>)" with the range clipped to that day's operating
  // hours. Falls back to the generic period band until the profile loads; when
  // the period is outside the center's hours it reads "(Outside operating hours)",
  // and a fully closed day reads "(Center closed)" — matching the appointment
  // detail page. Orders whose schedule is fixed (invoice sent or rejected, same
  // statuses as the detail page) are not flagged after a later operating-hours
  // change: they fall back to the generic period band instead.
  const SCHEDULE_FIXED_STATUSES = [
    "AWAITING_PAYMENT",
    "ORDER_ACCEPTED",
    "SCHEDULED",
    "THERAPIST_ASSIGNED",
    "THERAPIST_DISPATCHED",
    "IN_PROGRESS",
    "COMPLETED",
    "REVISIT_REQUESTED",
    "REJECTED",
  ];
  const formatPreferredSlot = (rawSlot, dateStr, status) => {
    if (!rawSlot) return "—";
    const key = String(rawSlot).toUpperCase();
    const periodName = key.charAt(0) + key.slice(1).toLowerCase();
    const band = PERIOD_BANDS[key];
    const hours = getCenterHoursForDate(dateStr);
    const scheduleFixed = SCHEDULE_FIXED_STATUSES.includes(
      String(status || "").toUpperCase(),
    );
    if (band && hours) {
      if (hours.is_open) {
        const start = Math.max(band.start, hhmmToMin(hours.start));
        const end = Math.min(band.end, hhmmToMin(hours.end));
        if (start < end) return `${periodName} (${minsToLabel12(start)} - ${minsToLabel12(end)})`;
        if (!scheduleFixed) return `${periodName} (Outside operating hours)`; // open day, period out of hours
      } else if (!scheduleFixed) {
        return `${periodName} (Center closed)`; // center closed that day
      }
    }
    const staticRange =
      key === "MORNING" ? "8:00 AM - 12:00 PM"
        : key === "AFTERNOON" ? "12:00 PM - 5:00 PM"
        : key === "EVENING" ? "5:00 PM - 9:00 PM"
        : null;
    return staticRange ? `${periodName} (${staticRange})` : periodName;
  };

  const ordersFromApi = apiOrdersArray.map((order) => ({
    id: order.booking_number || order.id || "—",
    patientName: order.patient?.name || order.patient_name || [order.patient?.full_name, order.patient?.surname].filter(Boolean).join(" ") || "—",
    patientAvatar: order.patient?.avatar_presigned_url || null,
    // The child's composed name on a child booking (empty otherwise) — the Child Name column.
    // `patientName` stays the account holder.
    childName: composeBookingChildName(order),
    patientUserId: order.patient_id || order.patient?.user_id || order.patient?.id || null,
    bookingId: order.id || order.booking_id || null,
    requestDate: order.created_at ? tzDateNumeric(new Date(order.created_at)) : "—",
    preferredDate: order.preferred_date ? tzDateNumeric(new Date(order.preferred_date)) : "—",
    preferredTime: formatPreferredSlot(order.preferred_time_slot, order.preferred_date, order.status),
    serviceName: order.services?.map((s) => s.service_name).join(", ") || order.service_name || "—",
    gender: order.preferred_therapist_gender || "—",
    urgency: order.is_urgent ? "Urgent" : "Not Urgent",
    // Show the ACTUAL service location (services can be switched to On-Site
    // independently of the patient's preferred_location). Any Home Visit
    // service ⇒ Home Visit, otherwise On-Site. Fall back to preferred_location
    // only when no services exist yet (early states before services are added).
    location: (() => {
      const svcs = order.services || [];
      const hasSvcLocation = svcs.some((s) => s?.location);
      if (hasSvcLocation) {
        return svcs.some((s) => String(s.location).toUpperCase() === "HOME_VISIT")
          ? "Home Visit"
          : "On-Site";
      }
      return order.preferred_location === "HOME_VISIT" ? "Home Visit" : "On-Site";
    })(),
    status: order.status || "REQUEST_SUBMITTED",
    serviceDate: order.scheduled_date ? tzDateNumeric(new Date(order.scheduled_date)) : (order.preferred_date ? tzDateNumeric(new Date(order.preferred_date)) : "—"),
    serviceTime: (() => {
      const t = order.scheduled_time;
      if (t) {
        // Convert a 24-hour "HH:mm[:ss]" time to 12-hour "h:mm AM/PM".
        const parts = String(t).split(":");
        if (parts.length >= 2) {
          const h = parseInt(parts[0], 10);
          const m = parts[1];
          const period = h >= 12 ? "PM" : "AM";
          const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
          return `${dh}:${m} ${period}`;
        }
        return t;
      }
      return order.preferred_time_slot
        ? `${order.preferred_time_slot.charAt(0).toUpperCase() + order.preferred_time_slot.slice(1).toLowerCase()}`
        : "—";
    })(),
    technician: order.assigned_therapist_name || order.therapist?.name || "No technician",
    // Net of any loyalty-point discount so the column matches the invoice modal
    // Total (which subtracts the redeemed points). Without this the table showed
    // the pre-discount gross while the invoice showed the net.
    totalPrice:
      order.total_amount != null
        ? `IQD ${formatWholeAmount(Math.max(0, Number(order.total_amount) - Number(order.loyalty_discount || 0)))}`
        : "—",
    // Normalize to one of the badge labels getStatusColor knows (Payment
    // Complete / COD / Pending) so the payment-status badge renders with a
    // consistent color across ALL tabs (raw values like "PENDING" would
    // otherwise map to no color and show as plain text).
    paymentStatus: ["PAID", "COMPLETED"].includes(String(order.payment_status || "").toUpperCase())
      ? "Completed"
      : order.payment_method?.toUpperCase() === "COD"
        ? "COD"
        : "Pending",
    // Report status for the Completed tab: whether the post-treatment
    // report/result has been shared with the patient.
    reportStatus: String(order.report_status || "").toUpperCase() === "SENT" ? "Sent" : "Not Sent",
    // SLA (center-action) fields for the order-management SLA column.
    slaDueAt: order.sla_due_at || null,
    slaIsUrgent: order.sla_is_urgent || false,
    // Patient-confirmation SLA (Awaiting Payment tab) — dynamic countdown window.
    patientConfirmDueAt:
      order.timer_started_at && order.timer_duration_seconds
        ? new Date(
            new Date(order.timer_started_at).getTime() +
              Number(order.timer_duration_seconds) * 1000,
          ).toISOString()
        : null,
    raw: order,
  }));
  const hasApiData = ordersFromApi.length > 0;

  // Bind appointments from API (normalize shape for the existing UI)
  const rawAppointments = Array.isArray(apiAppointments) ? apiAppointments : (apiAppointments?.orders || apiAppointments?.data?.orders || []);
  const appointments = rawAppointments.map((a) => {
    const patient = a?.patient || {};
    const name = `${patient?.full_name || ""} ${
      patient?.surname || ""
    }`.trim();
    const avatar = patient?.avatar_presigned_url || patient?.avatar || user;
    // Determine typeIcon based on consultation_type
    const typeIcon =
      a.consultation_type === "video"
        ? "video"
        : a.consultation_type === "audio"
          ? "mic"
          : "message";
    const type = a.consultation_type === "video" ? "Online Consult" : "On-Site";
    return {
      id: a.id,
      name,
      avatar,
      location: a?.location || a?.onsite_details || "",
      date: a?.appointment_date || a?.booking_date || "",
      time:
        a?.formatted_time ||
        (a?.appointment_time_slot
          ? a.appointment_time_slot.charAt(0).toUpperCase() +
            a.appointment_time_slot.slice(1)
          : ""),
      typeIcon,
      type,
      status: a?.status || "",
      raw: a,
    };
  });

  // Sort appointments (filtering is done server-side)
  const sortedAppointments = useMemo(() => {
    // Sort appointments
    const sorted = [...appointments].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [appointments, sortField, sortDirection]);

  // Pagination
  // const totalPages = Math.ceil(
  //   filteredAndSortedAppointments.length / itemsPerPage
  // );
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const paginatedAppointments = filteredAndSortedAppointments.slice(
  //   startIndex,
  //   startIndex + itemsPerPage
  // );

  const paginatedAppointments = sortedAppointments;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return null;
    try {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Format status text for display
  const formatStatusText = (status) => {
    if (!status) return "";
    // Convert to display format
    const statusDisplayMap = {
      pending: "Pending",
      upcoming: "Upcoming",
      ongoing: "Ongoing",
      completed: "Completed",
      cancelled: "Cancelled",
      reschedule_requested: "Reschedule Requested",
      "reschedule-requested": "Reschedule Requested",
    };

    const lowerStatus = status.toLowerCase();
    if (statusDisplayMap[lowerStatus]) {
      return statusDisplayMap[lowerStatus];
    }

    // Capitalize first letter of each word for other statuses
    return status
      .split(/[_\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Normalize status value for API (convert display format to API format)
  const normalizeStatusForAPI = (status) => {
    if (!status || status === "All") return null;
    // Convert display format to API format
    const statusMap = {
      Pending: "pending",
      Upcoming: "upcoming",
      Ongoing: "ongoing",
      Completed: "completed",
      Cancelled: "cancelled",
      "Reschedule Requested": "reschedule_requested",
      reschedule_requested: "reschedule_requested",
      pending: "pending",
      upcoming: "upcoming",
      ongoing: "ongoing",
      completed: "completed",
      cancelled: "cancelled",
      // "Results / Pending Results" tab — pseudo-status the backend expands to
      // status=COMPLETED AND payment_status IN (COMPLETED, PAID). Sent verbatim
      // (the gateway/service recognise it), so map it explicitly instead of
      // letting the space-replace fallback mangle it.
      "Results / Pending Results": "RESULTS_PENDING_OR_SENT",
      "results / pending results": "RESULTS_PENDING_OR_SENT",
      RESULTS_PENDING_OR_SENT: "RESULTS_PENDING_OR_SENT",
    };
    return statusMap[status] || status.toLowerCase().replace(/\s+/g, "_");
  };

  // Function to fetch appointments with current filters
  // Optional parameters allow overriding filter values (useful for tab clicks and reset)
  // Use undefined as default to distinguish between "not provided" and "explicitly null"
  const fetchAppointmentsWithFilters = (
    pageNum = currentPage,
    statusOverride = undefined,
    dateOverride = undefined,
    searchOverride = undefined,
    locationOverride = undefined,
  ) => {
    // Use override values if provided (including null), otherwise use current state
    // undefined means "use state value", null means "explicitly clear/no filter"
    const dateToUse = dateOverride !== undefined ? dateOverride : date;
    const statusToUse =
      statusOverride !== undefined ? statusOverride : statusFilter;
    const searchToUse =
      searchOverride !== undefined ? searchOverride : searchTerm;
    const locationToUse =
      locationOverride !== undefined ? locationOverride : locationFilter;

    const formattedDate = formatDate(dateToUse);
    const normalizedStatus = normalizeStatusForAPI(statusToUse);

    // Build params object - only include defined values
    const params = {
      page: pageNum,
      limit: itemsPerPage,
    };

    if (locationToUse) {
      params.location = locationToUse;
    }

    // Only add date if it's a valid formatted date string (not null/undefined/empty)
    if (
      formattedDate &&
      formattedDate !== null &&
      formattedDate !== "null" &&
      formattedDate !== ""
    ) {
      params.date = formattedDate;
    }

    if (searchToUse && searchToUse.trim()) {
      params.search = searchToUse.trim();
    }

    if (normalizedStatus) {
      params.status = normalizedStatus;
    }

    dispatch(fetchDoctorAppointmentsReducer(params))
      .then((res) => {
        setCurrentPage(pageNum);
        // Total pages will be updated in Redux state automatically
      })
      .catch((error) => {
        console.error("❌ Error fetching appointments:", error);
      });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchAppointmentsWithFilters(page);
  };

  const resetFilters = () => {
    // Reset state values first
    setSearchTerm("");
    setStatusFilter("Received");
    setLocationFilter("");
    setDate(null);
    setSearchResults([]);
    setShowSearchDropdown(false);
    setCurrentPage(1);
    setActiveTab("Received");

    // Immediately fetch with reset values
    fetchAppointmentsWithFilters(1, "Received", null, "", "");
  };

  // Debounced search typeahead — matches booking number OR patient name across
  // all statuses; results shown in a dropdown, clicking navigates to details.
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      return;
    }
    const handler = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const token = sessionStorage.getItem("usr_1a2b3c");
        const baseURL = process.env.REACT_APP_API_URL || "";
        const res = await fetch(
          `${baseURL}${APP_PATH.physioOrders}?search=${encodeURIComponent(term)}&limit=10`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await res.json();
        setSearchResults(json?.data?.orders || []);
        setShowSearchDropdown(true);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // ── Invoice (Print) — rendered by the shared <PhysioInvoiceModal>, which
  // fetches the order + center profile and handles printing. Order Management
  // and the Dashboard use the exact same component for a consistent invoice. ──
  const openInvoice = (item) => {
    const orderId = item.raw?.id || item.rawId || item.id;
    if (!orderId) return;
    setInvoiceOrderId(orderId);
    setShowInvoiceModal(true);
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  const joinAppointment = async (id) => {
    try {
      const result = await dispatch(
        joinDoctorAppointmentReducer({ appointmentId: id }),
      ).unwrap();
      console.log("result:", result);
      // reducer returns response.data.data (normalized) so join_url should be on result
      const joinUrl = result?.join_url || result?.data?.join_url;
      if (joinUrl) {
        // open in a new tab
        window.open(joinUrl, "_blank");
      } else {
        console.warn(
          "No join_url received from joinDoctorAppointmentReducer",
          result,
        );
      }
    } catch (err) {
      console.error("Failed to join appointment:", err);
    }
  };

  // Initial load with default filters
  useEffect(() => {
    fetchAppointmentsWithFilters(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the center profile once on mount so the preferred-time-slot ranges in
  // the table can be clipped to the center's operating hours.
  useEffect(() => {
    if (centerProfile) return;
    const token = sessionStorage.getItem("usr_1a2b3c");
    const baseURL = process.env.REACT_APP_API_URL || "";
    fetch(`${baseURL}/api/v1/physiotherapist/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((pj) => { if (pj?.data) setCenterProfile(pj.data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Front-face tooltip via event delegation. Works for EVERY tab's table
  // (the inline `.testNames` markup is identical across tabs) without editing
  // each block. On hover we read the service names from the hidden
  // `.testNamesList` and anchor a single position:fixed tooltip to the cell.
  useEffect(() => {
    const cls = styles["testNames"];
    if (!cls) return;

    const showFor = (host) => {
      const listEl = host.querySelector("." + styles["testNamesList"]);
      if (!listEl) return;
      const items = Array.from(listEl.querySelectorAll("li"))
        .slice(1) // skip the "Services" header li
        .map((li) => li.textContent.trim())
        .filter(Boolean);
      if (!items.length) return;
      const rect = host.getBoundingClientRect();
      // Prefer below the cell; flip above if not enough room.
      const belowSpace = window.innerHeight - rect.bottom;
      const y = belowSpace < 200 ? rect.top - 8 : rect.bottom + 4;
      const flipUp = belowSpace < 200;
      setSvcTip({ items, x: rect.left, y, flipUp });
    };

    const onOver = (e) => {
      const host = e.target.closest("." + cls);
      if (host) showFor(host);
    };
    const onOut = (e) => {
      const host = e.target.closest("." + cls);
      if (host && !host.contains(e.relatedTarget)) setSvcTip(null);
    };
    const onScroll = () => setSvcTip(null);

    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  // Silent refresh: Listen for new order notifications and re-fetch data
  useEffect(() => {
    const handleOrderRefresh = (event) => {
      console.log('[PHYSIO_ORDER_MGMT] Silent refresh triggered by notification:', event.detail?.notification_type);
      fetchAppointmentsWithFilters(currentPage);
    };

    window.addEventListener('physio-order-refresh', handleOrderRefresh);
    return () => {
      window.removeEventListener('physio-order-refresh', handleOrderRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, date, searchTerm]);

  // useEffect(() => {
  //   dispatch(fetchDoctorAppointmentsReducer()).then((res)=>{
  //   setTotalPages(res?.payload?.pagination?.total_pages)
  // })

  //   // dispatch(joinDoctorAppointmentReducer({appointmentId:2}))
  // }, []);

  const handlePageChange1 = (data) => {
    const nextPage = data + 1;
    fetchAppointmentsWithFilters(nextPage);
  };

  const handlePageChange11 = (data) => {
    const prevPage = data - 1;
    if (prevPage >= 1) {
      fetchAppointmentsWithFilters(prevPage);
    }
  };
  const handleBackDetail = (state, data) => {
    setShowDetails(state);
    setSelectedAppointment(data);
  };

  const handleReschedules = (data) => {
    setOpenDialogReschedule(true);
    setSelectedAppointment(data);
  };

  const handleRevisit = (data) => {
    setOpenPhysioDialogRevisit(true);
    setSelectedAppointment(data);
  };

  // const CreateFollowUp = (data) => {
  //   setOpenDialogCreateFollowUp(true);
  //   setSelectedAppointment(data);
  // };

  const getStatusColor = (status) => {
    switch (status) {
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-completed";
      case "Cancelled":
        return "status-cancelled";
      case "Reschedule Requested":
        return "status-reschedule";
      case "Pending":
        return "status-pending"; // Peach — payment "Pending" badge (matches lab/imaging)
      case "COD":
        return "status-cod";
      case "Payment Complete":
        return "status-payment";
      case "Collected":
        return "status-payment";
      case "Lab":
        return "status-labtype";
      case "Home Visit":
        return "status-labtype";
      case "Imaging":
        return "status-imagingtype";
      case "On-Site":
        return "status-imagingtype";
      case "Urgent":
        return "status-red";
      case "Not Urgent":
        return "status-cod";
      case "Technician Dispatched":
        return "status-dispatched";
      case "Sent":
        return "status-dispatched";
      case "Not Sent":
        return "status-cod";
      default:
        return "";
    }
  };

  const orderManagementTabs = [
    "Received",
    "Assessment",
    "Awaiting Payment",
    "Order Accepted",
    "Scheduled",
    "In-Progress",
    "Completed",
    // Consolidated tab surfacing every fully-paid completed booking whether its
    // report is still pending or already sent (mirrors the lab app). Reuses the
    // Completed render block; backend pseudo-status RESULTS_PENDING_OR_SENT.
    "Results / Pending Results",
    "Revisit Requests",
    "Rejected",
  ];

  const receivedTableData = [
    {
      id: "PT-0001",
      patientName: "Sebastian Rockford",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName:
        "Post-Operative Ortho Rehabilitation, Shoulder Rehabilitation",
      gender: "Male",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0002",
      patientName: "Lyla Khalid",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Knee Osteoarthritis (OA Knee) Physiotherapy",
      gender: "Male",
      urgency: "",
      location: "On-Site",
    },
    {
      id: "PT-0003",
      patientName: "Kamla Khalid",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Low Back Pain (LBA) Rehabilitation Program",
      gender: "Male",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0004",
      patientName: "Remy Dawson",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Cervical Spondylosis Physiotherapy",
      gender: "Male",
      urgency: "",
      location: "On-Site",
    },
    {
      id: "PT-0005",
      patientName: "Kyle Idris",
      requestDate: "10-12-2025",
      preferredDate: "10-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Shoulder Rehabilitation",
      gender: "Male",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0006",
      patientName: "Olivia Robison",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Lumbar Disc Prolapse (VDP) Rehabilitation",
      gender: "Male",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0007",
      patientName: "Ruddy Backwoods",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Post-Fracture Stiffness Management",
      gender: "Male",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0008",
      patientName: "Kevin Andresen",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Ligament Injury Rehabilitation (ACL / MCL / PCL)",
      gender: "Male",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0009",
      patientName: "Vivian Luther",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Joint Mobilization & Stabilization Exercises",
      gender: "Male",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0010",
      patientName: "Zack Parker",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Pain Management for Musculoskeletal Disorders",
      gender: "Male",
      urgency: "Urgent",
      location: "On-Site",
    },
  ];

  const awaitingPatientConfirmationTableData = [
    {
      id: "PT-0001",
      patientName: "Sebastian Rockford",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName:
        "Post-Operative Ortho Rehabilitation, Shoulder Rehabilitation",
      gender: "Male",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0002",
      patientName: "Lyla Khalid",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Knee Osteoarthritis (OA Knee) Physiotherapy",
      gender: "Male",
      urgency: "",
      location: "On-Site",
    },
    {
      id: "PT-0003",
      patientName: "Kamla Khalid",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Low Back Pain (LBA) Rehabilitation Program",
      gender: "Male",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0004",
      patientName: "Remy Dawson",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Cervical Spondylosis Physiotherapy",
      gender: "Male",
      urgency: "",
      location: "On-Site",
    },
    {
      id: "PT-0005",
      patientName: "Kyle Idris",
      requestDate: "10-12-2025",
      preferredDate: "10-12-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Shoulder Rehabilitation",
      gender: "Male",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0006",
      patientName: "Olivia Robison",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Lumbar Disc Prolapse (VDP) Rehabilitation",
      gender: "Male",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0007",
      patientName: "Ruddy Backwoods",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Post-Fracture Stiffness Management",
      gender: "Male",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0008",
      patientName: "Kevin Andresen",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Ligament Injury Rehabilitation (ACL / MCL / PCL)",
      gender: "Male",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0009",
      patientName: "Vivian Luther",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Joint Mobilization & Stabilization Exercises",
      gender: "Male",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0010",
      patientName: "Zack Parker",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTime: "Morning (8:00 AM - 12:00 PM)",
      serviceName: "Pain Management for Musculoskeletal Disorders",
      gender: "Male",
      urgency: "Urgent",
      location: "On-Site",
    },
  ];

  const generateInvoiceTableData = [
    {
      id: "PT-0001",
      patientName: "Sebastian Rockford",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName:
        "Post-Operative Ortho Rehabilitation, Shoulder Rehabilitation",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0002",
      patientName: "Lyla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Knee Osteoarthritis (OA Knee) Physiotherapy",
      urgency: "",
      location: "On-Site",
    },
    {
      id: "PT-0003",
      patientName: "Kamla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Low Back Pain (LBA) Rehabilitation Program",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0004",
      patientName: "Remy Dawson",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Cervical Spondylosis Physiotherapy",
      urgency: "",
      location: "On-Site",
    },
    {
      id: "PT-0005",
      patientName: "Kyle Idris",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Shoulder Rehabilitation",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0006",
      patientName: "Olivia Robinson",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Lumbar Disc Prolapse (VDP) Rehabilitation",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0007",
      patientName: "Ruddy Backwoods",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Post-Fracture Stiffness Management",
      urgency: "",
      location: "Home Visit",
    },
    {
      id: "PT-0008",
      patientName: "Kevin Andresen",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Ligament Injury Rehabilitation (ACL / MCL / PCL)",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0009",
      patientName: "Vivian Luther",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Joint Mobilization & Stabilization Exercises",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "PT-0010",
      patientName: "Zack Parker",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Pain Management for Musculoskeletal Disorders",
      urgency: "Urgent",
      location: "On-Site",
    },
  ];

  const awaitingPaymentTableData = [
    {
      id: "PT-0001",
      patientName: "Sebastian Rockford",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName:
        "Post-Operative Ortho Rehabilitation, Shoulder Rehabilitation",
      urgency: "",
      location: "Home Visit",
      technician: "Rahul Mehta",
      totalPrice: "15,000",
    },
    {
      id: "PT-0002",
      patientName: "Lyla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Knee Osteoarthritis (OA Knee) Physiotherapy",
      urgency: "",
      location: "On-Site",
      technician: "",
      totalPrice: "30,000",
    },
    {
      id: "PT-0003",
      patientName: "Kamla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Low Back Pain (LBA) Rehabilitation Program",
      urgency: "",
      location: "Home Visit",
      technician: "Casie Philip",
      totalPrice: "30,000",
    },
    {
      id: "PT-0004",
      patientName: "Remy Dawson",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Cervical Spondylosis Physiotherapy",
      urgency: "",
      location: "On-Site",
      technician: "",
      totalPrice: "10,000",
    },
    {
      id: "PT-0005",
      patientName: "Kyle Idris",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Shoulder Rehabilitation",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,50,000",
    },
    {
      id: "PT-0006",
      patientName: "Olivia Robinson",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Lumbar Disc Prolapse (VDP) Rehabilitation",
      urgency: "",
      location: "Home Visit",
      technician: "Naman Bansal",
      totalPrice: "10,000",
    },
    {
      id: "PT-0007",
      patientName: "Ruddy Backwoods",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Post-Fracture Stiffness Management",
      urgency: "",
      location: "Home Visit",
      technician: "Rohit Kapoor",
      totalPrice: "10,000",
    },
    {
      id: "PT-0008",
      patientName: "Kevin Andresen",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Ligament Injury Rehabilitation (ACL / MCL / PCL)",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,00,000",
    },
    {
      id: "PT-0009",
      patientName: "Vivian Luther",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Joint Mobilization & Stabilization Exercises",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,50,000",
    },
    {
      id: "PT-0010",
      patientName: "Zack Parker",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Pain Management for Musculoskeletal Disorders",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,00,000",
    },
  ];

  const orderAcceptedTableData = [
    {
      id: "PT-0001",
      patientName: "Sebastian Rockford",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName:
        "Post-Operative Ortho Rehabilitation, Shoulder Rehabilitation",
      urgency: "",
      location: "Home Visit",
      technician: "Rahul Mehta",
      totalPrice: "15,000",
      paymentStatus: "COD",
    },
    {
      id: "PT-0002",
      patientName: "Lyla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Knee Osteoarthritis (OA Knee) Physiotherapy",
      urgency: "",
      location: "On-Site",
      technician: "",
      totalPrice: "30,000",
      paymentStatus: "COD",
    },
    {
      id: "PT-0003",
      patientName: "Kamla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Low Back Pain (LBA) Rehabilitation Program",
      urgency: "",
      location: "Home Visit",
      technician: "Casie Philip",
      totalPrice: "30,000",
      paymentStatus: "COD",
    },
    {
      id: "PT-0004",
      patientName: "Remy Dawson",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Cervical Spondylosis Physiotherapy",
      urgency: "",
      location: "On-Site",
      technician: "",
      totalPrice: "10,000",
      paymentStatus: "COD",
    },
    {
      id: "PT-0005",
      patientName: "Kyle Idris",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Shoulder Rehabilitation",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,50,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0006",
      patientName: "Olivia Robinson",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Lumbar Disc Prolapse (VDP) Rehabilitation",
      urgency: "",
      location: "Home Visit",
      technician: "Naman Bansal",
      totalPrice: "10,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0007",
      patientName: "Ruddy Backwoods",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Post-Fracture Stiffness Management",
      urgency: "",
      location: "Home Visit",
      technician: "Rohit Kapoor",
      totalPrice: "10,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0008",
      patientName: "Kevin Andresen",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Ligament Injury Rehabilitation (ACL / MCL / PCL)",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,00,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0009",
      patientName: "Vivian Luther",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Joint Mobilization & Stabilization Exercises",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,50,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0010",
      patientName: "Zack Parker",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Pain Management for Musculoskeletal Disorders",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,00,000",
      paymentStatus: "Payment Complete",
    },
  ];

  const inProgressTableData = [
    {
      id: "PT-0001",
      patientName: "Sebastian Rockford",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName:
        "Post-Operative Ortho Rehabilitation, Shoulder Rehabilitation",
      urgency: "",
      location: "Home Visit",
      technician: "Rahul Mehta",
      totalPrice: "15,000",
      sampleStatus: "Pending",
      paymentStatus: "COD",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "PT-0002",
      patientName: "Lyla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Knee Osteoarthritis (OA Knee) Physiotherapy",
      urgency: "",
      location: "On-Site",
      technician: "",
      totalPrice: "30,000",
      sampleStatus: "Pending",
      paymentStatus: "COD",
      technicianStatus: "",
    },
    {
      id: "PT-0003",
      patientName: "Kamla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Low Back Pain (LBA) Rehabilitation Program",
      urgency: "",
      location: "Home Visit",
      technician: "Casie Philip",
      totalPrice: "30,000",
      sampleStatus: "Pending",
      paymentStatus: "COD",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "PT-0004",
      patientName: "Remy Dawson",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Cervical Spondylosis Physiotherapy",
      urgency: "",
      location: "On-Site",
      technician: "",
      totalPrice: "10,000",
      sampleStatus: "Pending",
      paymentStatus: "COD",
      technicianStatus: "",
    },
    {
      id: "PT-0005",
      patientName: "Kyle Idris",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Shoulder Rehabilitation",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,50,000",
      sampleStatus: "Pending",
      paymentStatus: "Payment Complete",
      technicianStatus: "",
    },
    {
      id: "PT-0006",
      patientName: "Olivia Robinson",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Lumbar Disc Prolapse (VDP) Rehabilitation",
      urgency: "",
      location: "Home Visit",
      technician: "Naman Bansal",
      totalPrice: "10,000",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "PT-0007",
      patientName: "Ruddy Backwoods",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Post-Fracture Stiffness Management",
      urgency: "",
      location: "Home Visit",
      technician: "Rohit Kapoor",
      totalPrice: "10,000",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "PT-0008",
      patientName: "Kevin Andresen",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Ligament Injury Rehabilitation (ACL / MCL / PCL)",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,00,000",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "",
    },
    {
      id: "PT-0009",
      patientName: "Vivian Luther",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Joint Mobilization & Stabilization Exercises",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,50,000",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "",
    },
    {
      id: "PT-0010",
      patientName: "Zack Parker",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Pain Management for Musculoskeletal Disorders",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,00,000",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "",
    },
  ];

  const completedTableData = [
    {
      id: "PT-0001",
      patientName: "Sebastian Rockford",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName:
        "Post-Operative Ortho Rehabilitation, Shoulder Rehabilitation",
      urgency: "",
      location: "Home Visit",
      technician: "Rahul Mehta",
      totalPrice: "15,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0002",
      patientName: "Lyla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Knee Osteoarthritis (OA Knee) Physiotherapy",
      urgency: "",
      location: "On-Site",
      technician: "",
      totalPrice: "30,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0003",
      patientName: "Kamla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Low Back Pain (LBA) Rehabilitation Program",
      urgency: "",
      location: "Home Visit",
      technician: "Casie Philip",
      totalPrice: "30,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0004",
      patientName: "Remy Dawson",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Cervical Spondylosis Physiotherapy",
      urgency: "",
      location: "On-Site",
      technician: "",
      totalPrice: "10,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0005",
      patientName: "Kyle Idris",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Shoulder Rehabilitation",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,50,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0006",
      patientName: "Olivia Robinson",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Lumbar Disc Prolapse (VDP) Rehabilitation",
      urgency: "",
      location: "Home Visit",
      technician: "Naman Bansal",
      totalPrice: "10,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0007",
      patientName: "Ruddy Backwoods",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Post-Fracture Stiffness Management",
      urgency: "",
      location: "Home Visit",
      technician: "Rohit Kapoor",
      totalPrice: "10,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0008",
      patientName: "Kevin Andresen",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Ligament Injury Rehabilitation (ACL / MCL / PCL)",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,00,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0009",
      patientName: "Vivian Luther",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Joint Mobilization & Stabilization Exercises",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,50,000",
      paymentStatus: "Payment Complete",
    },
    {
      id: "PT-0010",
      patientName: "Zack Parker",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Pain Management for Musculoskeletal Disorders",
      urgency: "Urgent",
      location: "On-Site",
      technician: "",
      totalPrice: "1,00,000",
      paymentStatus: "Payment Complete",
    },
  ];

  const rejectedTableData = [
    {
      id: "PT-0001",
      patientName: "Sebastian Rockford",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName:
        "Post-Operative Ortho Rehabilitation, Shoulder Rehabilitation",
      urgency: "",
      location: "Home Visit",
      totalPrice: "15,000",
    },
    {
      id: "PT-0002",
      patientName: "Lyla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Knee Osteoarthritis (OA Knee) Physiotherapy",
      urgency: "",
      location: "On-Site",
      totalPrice: "30,000",
    },
    {
      id: "PT-0003",
      patientName: "Kamla Khalid",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Low Back Pain (LBA) Rehabilitation Program",
      urgency: "",
      location: "Home Visit",
      totalPrice: "30,000",
    },
    {
      id: "PT-0004",
      patientName: "Remy Dawson",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Cervical Spondylosis Physiotherapy",
      urgency: "",
      location: "On-Site",
      totalPrice: "10,000",
    },
    {
      id: "PT-0005",
      patientName: "Kyle Idris",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Shoulder Rehabilitation",
      urgency: "Urgent",
      location: "On-Site",
      totalPrice: "1,50,000",
    },
    {
      id: "PT-0006",
      patientName: "Olivia Robinson",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Lumbar Disc Prolapse (VDP) Rehabilitation",
      urgency: "",
      location: "Home Visit",
      totalPrice: "10,000",
    },
    {
      id: "PT-0007",
      patientName: "Ruddy Backwoods",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Post-Fracture Stiffness Management",
      urgency: "",
      location: "Home Visit",
      totalPrice: "10,000",
    },
    {
      id: "PT-0008",
      patientName: "Kevin Andresen",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Ligament Injury Rehabilitation (ACL / MCL / PCL)",
      urgency: "Urgent",
      location: "On-Site",
      totalPrice: "1,00,000",
    },
    {
      id: "PT-0009",
      patientName: "Vivian Luther",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Joint Mobilization & Stabilization Exercises",
      urgency: "Urgent",
      location: "On-Site",
      totalPrice: "1,50,000",
    },
    {
      id: "PT-0010",
      patientName: "Zack Parker",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      serviceName: "Pain Management for Musculoskeletal Disorders",
      urgency: "Urgent",
      location: "On-Site",
      totalPrice: "1,00,000",
    },
  ];

  return (
    <PhysioDashboardLayout
      currentPage="physio_ordermanagement"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      <>
      {/* Front-face service-name tooltip (position:fixed so it is never clipped
          by the table's horizontal scroll box). */}
      {svcTip && (
        <div
          className={styles["testNamesList"]}
          style={{
            position: "fixed",
            left: svcTip.x,
            top: svcTip.y,
            display: "block",
            zIndex: 99999,
            pointerEvents: "none",
            transform: svcTip.flipUp ? "translateY(-100%)" : "none",
          }}
        >
          <ul>
            <li><span>Services</span></li>
            {svcTip.items.map((svc, i) => (
              <li key={i}>{svc}</li>
            ))}
          </ul>
        </div>
      )}
      <PhysioDialogRevisit
              open={openPhysioDialogRevisit}
              onClose={() => {
                setOpenPhysioDialogRevisit(false);
              }}
              selectedAppointment={selectedAppointment}
              onSuccess={() => fetchAppointmentsWithFilters(currentPage)}
            />
        <div className={styles["apt-page-header"]}>
          <h1>Order Management</h1>
        </div>
        <div className={styles["apt-appointments-section"]}>
          <div className={styles["apt-section-header"]}>
            <h2>{t("appointments")}</h2>
            <div className={styles["apt-top-actions"]}>
              <button className={styles["exportBtn"]}>
                <FiDownload /> Export
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className={styles["apt-filters"]}>
            <div className={styles["apt-filter-group"]}>
              {/* <span className={styles["apt-filter-icon-wrap"]}>
                  <Filter className={styles["apt-filter-icon"]} />
                </span> */}
              <div className={styles["apt-search-wrapper"]} style={{ position: "relative" }}>
                <Search className={styles["apt-search-icon"]} />
                <input
                  type="text"
                  placeholder="Search appointment id or patient..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => { if (searchResults.length > 0) setShowSearchDropdown(true); }}
                  onBlur={() => setTimeout(() => setShowSearchDropdown(false), 150)}
                  className={styles["apt-search-input"]}
                />
                {showSearchDropdown && searchTerm.trim() && (
                  <div
                    style={{
                      position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
                      background: "#fff", border: "1px solid #e5e7eb", borderRadius: "8px",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: "300px", overflowY: "auto",
                    }}
                  >
                    {searchLoading ? (
                      <div style={{ padding: "12px 14px", fontSize: "13px", color: "#6b7280" }}>Searching...</div>
                    ) : searchResults.length === 0 ? (
                      <div style={{ padding: "12px 14px", fontSize: "13px", color: "#6b7280" }}>No orders found</div>
                    ) : (
                      searchResults.map((o) => {
                        const p = o.patient;
                        const pName = p ? `${p.full_name || ""} ${p.surname || ""}`.trim() : "";
                        return (
                          <button
                            type="button"
                            key={o.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setShowSearchDropdown(false);
                              navigate(`/physio_appointment_detail?id=${o.id}`);
                            }}
                            style={{
                              display: "flex", alignItems: "center", gap: "10px", width: "100%",
                              padding: "10px 14px", background: "none", border: "none",
                              borderBottom: "1px solid #f3f4f6", cursor: "pointer", textAlign: "left",
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: "13px", fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {composeBookingChildName(o) ||
                                  pName ||
                                  `Patient ${o.patient_id || ""}`}
                              </div>
                              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                {o.booking_number || `#${o.id}`}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <select
                value={locationFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setLocationFilter(val);
                  setCurrentPage(1);
                  fetchAppointmentsWithFilters(1, undefined, undefined, undefined, val);
                }}
                className={styles["apt-status-filter"]}
                style={{ paddingRight: "30px" }}
              >
                <option value="">All Locations</option>
                <option value="HOME_VISIT">Home Visit</option>
                <option value="ON_SITE">On-Site</option>
              </select>
              <div className={styles["date-wrapper"]}>
                <DatePicker
                  key={`datepicker-${date ? date.getTime() : "empty"}`}
                  className={styles["apt-date-filter"]}
                  selected={date}
                  onChange={(d) => { setDate(d); setCurrentPage(1); fetchAppointmentsWithFilters(1, undefined, d); }}
                  placeholderText="Select Date"
                  isClearable
                />
              </div>
              {/* <button className={styles["apt-apply-btn"]}>{t('appointments.filters.button.apply')}</button> */}
              {/* <button
                  className={styles["apt-apply-btn"]}
                  onClick={() => {
                    setCurrentPage(1);
                    fetchAppointmentsWithFilters(1);
                  }}
                >
                  {t("appointments.filters.button.apply")}
                </button> */}
              <button
                className={styles["apt-reset-btn"]}
                onClick={resetFilters}
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className={styles["apt-appointment-tabs"]}>
            {orderManagementTabs.map((tabItem, idx) => {
              return (
                <button
                  key={idx}
                  className={`${styles["apt-tab"]} ${
                    activeTab === tabItem ? styles["active"] : ""
                  }`}
                  onClick={() => {
                    setActiveTab(tabItem);
                    setStatusFilter(tabItem);
                    setCurrentPage(1);
                    fetchAppointmentsWithFilters(1, tabItem);
                  }}
                >
                  {tabItem}
                </button>
              );
            })}
          </div>

          {/* Table Received */}
          {activeTab === "Received" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Request Date</th>
                    <th>Preferred Date</th>
                    <th>Preferred Time slot</th>
                    <th>Service Name</th>
                    <th>Preferred Gender</th>
                    <th>Urgency</th>
                    <th>Preferred Location</th>
                    <th>SLA Time</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={12}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={12} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.requestDate}</td>
                        <td>{item.preferredDate}</td>
                        <td>{item.preferredTime}</td>
                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>
                        <td>{item.gender}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.location)]
                            }`}
                          >
                            {item.location}
                          </span>
                        </td>
                        <td>
                          <SlaTimer
                            dueAt={item.slaDueAt}
                            isUrgent={item.slaIsUrgent}
                            status={item.status}
                            visibleStatuses={["REQUEST_SUBMITTED"]}
                          />
                        </td>
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                              <button
                                className={styles["apt-view-btn"]}
                                title="View"
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                    fill="#155DFC"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                    fill="#155DFC"
                                  />
                                </svg>
                              </button>
                            </Link>

                            {PRINTABLE_STATUSES.includes(item.status ?? item.raw?.status) && (
                              <button
                                className={styles["apt-view-btn"]}
                                title="Print Invoice"
                                onClick={() => openInvoice(item)}
                                style={{ background: "none", border: "none", cursor: "pointer" }}
                              >
                                <FiPrinter size={18} color="#111827" />
                              </button>
                            )}

                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Awaiting Patient Confirmation Tab */}
          {activeTab === "Awaiting Patient Confirmation" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Request Date</th>
                    <th>Preferred Date</th>
                    <th>Preferred Time slot</th>
                    <th>Service Name</th>
                    <th>Preferred Gender</th>
                    <th>Urgency</th>
                    <th>Preferred Location</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={11}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.requestDate}</td>
                        <td>{item.preferredDate}</td>
                        <td>{item.preferredTime}</td>
                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>
                        <td>{item.gender}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.location)]
                            }`}
                          >
                            {item.location}
                          </span>
                        </td>
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                              <button
                                className={styles["apt-view-btn"]}
                                title="View"
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                    fill="#155DFC"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                    fill="#155DFC"
                                  />
                                </svg>
                              </button>
                            </Link>

                            {PRINTABLE_STATUSES.includes(item.status ?? item.raw?.status) && (
                              <button
                                className={styles["apt-view-btn"]}
                                title="Print Invoice"
                                onClick={() => openInvoice(item)}
                                style={{ background: "none", border: "none", cursor: "pointer" }}
                              >
                                <FiPrinter size={18} color="#111827" />
                              </button>
                            )}

                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Generate Invoice Tab */}
          {activeTab === "Generate Invoice" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Urgency</th>
                    <th>Location</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.serviceDate}</td>
                        <td>{item.serviceTime}</td>

                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.location)]
                            }`}
                          >
                            {item.location}
                          </span>
                        </td>
                        {/* ACTION COLUMN */}
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                            <button
                              className={styles["apt-view-btn"]}
                              title="View"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                  fill="#155DFC"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                  fill="#155DFC"
                                />
                              </svg>
                            </button>
                            </Link>

                            {PRINTABLE_STATUSES.includes(item.status ?? item.raw?.status) && (
                              <button
                                className={styles["apt-view-btn"]}
                                title="Print Invoice"
                                onClick={() => openInvoice(item)}
                                style={{ background: "none", border: "none", cursor: "pointer" }}
                              >
                                <FiPrinter size={18} color="#111827" />
                              </button>
                            )}

                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Awaiting Payment Tab */}
          {activeTab === "Awaiting Payment" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Urgency</th>
                    <th>Location</th>
                    <th>Technician</th>
                    <th>Total Price (IQD)</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={11}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.serviceDate}</td>
                        <td>{item.serviceTime}</td>

                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.location)]
                            }`}
                          >
                            {item.location}
                          </span>
                        </td>

                        <td>{item.technician}</td>

                        <td>{item.totalPrice}</td>

                        {/* ACTION COLUMN (UNCHANGED) */}
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                            <button
                              className={styles["apt-view-btn"]}
                              title="View"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                  fill="#155DFC"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                  fill="#155DFC"
                                />
                              </svg>
                            </button>
                            </Link>

                            {PRINTABLE_STATUSES.includes(item.status ?? item.raw?.status) && (
                              <button
                                className={styles["apt-view-btn"]}
                                title="Print Invoice"
                                onClick={() => openInvoice(item)}
                                style={{ background: "none", border: "none", cursor: "pointer" }}
                              >
                                <FiPrinter size={18} color="#111827" />
                              </button>
                            )}

                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Order Accepted tab  */}
          {activeTab === "Order Accepted" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Urgency</th>
                    <th>Location</th>
                    <th>Technician</th>
                    <th>Total Price (IQD)</th>
                    <th>Payment Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={12}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={12} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.serviceDate}</td>
                        <td>{item.serviceTime}</td>

                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.location)]
                            }`}
                          >
                            {item.location}
                          </span>
                        </td>

                        <td>{item.technician}</td>

                        <td>{item.totalPrice}</td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.paymentStatus)]
                            }`}
                          >
                            {item.paymentStatus}
                          </span>
                        </td>

                        {/* ACTION COLUMN (UNCHANGED) */}
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                            <button
                              className={styles["apt-view-btn"]}
                              title="View"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                  fill="#155DFC"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                  fill="#155DFC"
                                />
                              </svg>
                            </button>
                            </Link>

                            {PRINTABLE_STATUSES.includes(item.status ?? item.raw?.status) && (
                              <button
                                className={styles["apt-view-btn"]}
                                title="Print Invoice"
                                onClick={() => openInvoice(item)}
                                style={{ background: "none", border: "none", cursor: "pointer" }}
                              >
                                <FiPrinter size={18} color="#111827" />
                              </button>
                            )}

                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Assessment tab */}
          {activeTab === "Assessment" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Urgency</th>
                    <th>Location</th>
                    <th>Therapist Assigned</th>
                    <th>Total Price (IQD)</th>
                    <th>Payment Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={12}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={12} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.serviceDate}</td>
                        <td>{item.serviceTime}</td>
                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>
                        <td>{item.urgency && (<span className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}>{item.urgency}</span>)}</td>
                        <td><span className={`${styles["db-status"]} ${styles[getStatusColor(item.location)]}`}>{item.location}</span></td>
                        <td>{item.technician}</td>
                        <td>{item.totalPrice}</td>
                        <td><span className={`${styles["db-status"]} ${styles[getStatusColor(item.paymentStatus)]}`}>{item.paymentStatus}</span></td>
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                              <button className={styles["apt-view-btn"]} title="View">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" clipRule="evenodd" d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z" fill="#155DFC"/>
                                  <path fillRule="evenodd" clipRule="evenodd" d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z" fill="#155DFC"/>
                                </svg>
                              </button>
                            </Link>
                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Scheduled tab  */}
          {activeTab === "Scheduled" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Urgency</th>
                    <th>Location</th>
                    <th>Therapist Assigned</th>
                    <th>Total Price (IQD)</th>
                    <th>Payment Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={12}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={12} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.serviceDate}</td>
                        <td>{item.serviceTime}</td>

                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.urgency)]
                              }`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.location)]
                            }`}
                          >
                            {item.location}
                          </span>
                        </td>

                        <td>{item.technician}</td>

                        <td>{item.totalPrice}</td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${
                              styles[getStatusColor(item.paymentStatus)]
                            }`}
                          >
                            {item.paymentStatus}
                          </span>
                        </td>

                        {/* ACTION COLUMN (UNCHANGED) */}
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                            <button
                              className={styles["apt-view-btn"]}
                              title="View"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                  fill="#155DFC"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                  fill="#155DFC"
                                />
                              </svg>
                            </button>
                            </Link>

                            {PRINTABLE_STATUSES.includes(item.status ?? item.raw?.status) && (
                              <button
                                className={styles["apt-view-btn"]}
                                title="Print Invoice"
                                onClick={() => openInvoice(item)}
                                style={{ background: "none", border: "none", cursor: "pointer" }}
                              >
                                <FiPrinter size={18} color="#111827" />
                              </button>
                            )}

                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* In-Progress tab  */}
          {activeTab === "In-Progress" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Urgency</th>
                    <th>Location</th>
                    <th>Technician</th>
                    <th>Total Price (IQD)</th>
                    <th>Payment Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={12}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={12} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.serviceDate}</td>
                        <td>{item.serviceTime}</td>
                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${styles[getStatusColor(item.location)]}`}
                          >
                            {item.location}
                          </span>
                        </td>

                        <td>{item.technician}</td>

                        <td>{item.totalPrice}</td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${styles[getStatusColor(item.paymentStatus)]}`}
                          >
                            {item.paymentStatus}
                          </span>
                        </td>

                        {/* ACTION COLUMN (UNCHANGED) */}
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                            <button
                              className={styles["apt-view-btn"]}
                              title="View"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                  fill="#155DFC"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                  fill="#155DFC"
                                />
                              </svg>
                            </button>
                            </Link>

                            {PRINTABLE_STATUSES.includes(item.status ?? item.raw?.status) && (
                              <button
                                className={styles["apt-view-btn"]}
                                title="Print Invoice"
                                onClick={() => openInvoice(item)}
                                style={{ background: "none", border: "none", cursor: "pointer" }}
                              >
                                <FiPrinter size={18} color="#111827" />
                              </button>
                            )}

                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Completed Tab (also handles "Results / Pending Results" — both are
              COMPLETED bookings server-side; the latter additionally requires a
              completed payment via the RESULTS_PENDING_OR_SENT pseudo-status.
              Row layout is identical, so the single block is reused.) */}
          {(activeTab === "Completed" ||
            activeTab === "Results / Pending Results") && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Urgency</th>
                    <th>Location</th>
                    <th>Technician</th>
                    <th>Total Price (IQD)</th>
                    <th>Payment Status</th>
                    <th>Report Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={13}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={13} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.serviceDate}</td>
                        <td>{item.serviceTime}</td>
                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${styles[getStatusColor(item.location)]}`}
                          >
                            {item.location}
                          </span>
                        </td>

                        <td>{item.technician}</td>

                        <td>{item.totalPrice}</td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${styles[getStatusColor(item.paymentStatus)]}`}
                          >
                            {item.paymentStatus}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${styles[getStatusColor(item.reportStatus)]}`}
                          >
                            {item.reportStatus}
                          </span>
                        </td>

                        {/* ACTION COLUMN (UNCHANGED) */}
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                            <button
                              className={styles["apt-view-btn"]}
                              title="View"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                  fill="#155DFC"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                  fill="#155DFC"
                                />
                              </svg>
                            </button>
                            </Link>

                            {PRINTABLE_STATUSES.includes(item.status ?? item.raw?.status) && (
                              <button
                                className={styles["apt-view-btn"]}
                                title="Print Invoice"
                                onClick={() => openInvoice(item)}
                                style={{ background: "none", border: "none", cursor: "pointer" }}
                              >
                                <FiPrinter size={18} color="#111827" />
                              </button>
                            )}

                            <ChatIconWithBadge
                              className={styles["apt-view-btn"]}
                              title="Chat"
                              count={unreadForAppointment(item.bookingId ?? item.rawId ?? item.raw?.id)}
                              onClick={() => openChat(item)}
                            />

                            {/* Hide revisit clock icon when all sessions are complete for all services */}
                            {!(item.raw?.services?.length > 0 && item.raw.services.every(s => (s.sessions_completed || 0) >= (s.number_of_sessions || 1))) && (
                            <button
                              className={styles["apt-view-btn"]}
                              title="View"
                              onClick={() => {
                setSelectedAppointment(item);
                setOpenPhysioDialogRevisit(true);
              }}
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M21.6361 17.2561H19.0087V14.6287C19.0087 14.3965 18.9164 14.1737 18.7522 14.0094C18.588 13.8452 18.3652 13.7529 18.1329 13.7529C17.9006 13.7529 17.6779 13.8452 17.5136 14.0094C17.3494 14.1737 17.2571 14.3965 17.2571 14.6287V17.2561H14.6297C14.3974 17.2561 14.1747 17.3484 14.0104 17.5127C13.8462 17.6769 13.7539 17.8997 13.7539 18.1319C13.7539 18.3642 13.8462 18.587 14.0104 18.7512C14.1747 18.9155 14.3974 19.0077 14.6297 19.0077H17.2571V21.6351C17.2571 21.8674 17.3494 22.0902 17.5136 22.2544C17.6779 22.4187 17.9006 22.511 18.1329 22.511C18.3652 22.511 18.588 22.4187 18.7522 22.2544C18.9164 22.0902 19.0087 21.8674 19.0087 21.6351V19.0077H21.6361C21.8684 19.0077 22.0912 18.9155 22.2554 18.7512C22.4197 18.587 22.5119 18.3642 22.5119 18.1319C22.5119 17.8997 22.4197 17.6769 22.2554 17.5127C22.0912 17.3484 21.8684 17.2561 21.6361 17.2561Z"
                                  fill="#FF6900"
                                />
                                <path
                                  d="M11.1264 7.62238V11.6388L8.75565 14.0096C8.67201 14.0904 8.60528 14.187 8.55938 14.2939C8.51348 14.4007 8.48932 14.5157 8.48831 14.632C8.4873 14.7482 8.50946 14.8636 8.5535 14.9712C8.59754 15.0788 8.66257 15.1766 8.7448 15.2588C8.82703 15.3411 8.92481 15.4061 9.03245 15.4501C9.14008 15.4942 9.2554 15.5163 9.37169 15.5153C9.48798 15.5143 9.6029 15.4902 9.70976 15.4443C9.81661 15.3984 9.91325 15.3316 9.99404 15.248L12.6214 12.6206C12.7857 12.4564 12.878 12.2337 12.8781 12.0014V7.62238C12.8781 7.39011 12.7858 7.16734 12.6215 7.0031C12.4573 6.83885 12.2345 6.74658 12.0023 6.74658C11.77 6.74658 11.5472 6.83885 11.383 7.0031C11.2187 7.16734 11.1264 7.39011 11.1264 7.62238Z"
                                  fill="#FF6900"
                                />

                                <path
                                  d="M14.391 20.4301C12.5285 20.955 10.5446 20.8474 8.74988 20.1241C6.95517 19.4008 5.45103 18.1027 4.47304 16.433C3.49505 14.7634 3.09844 12.8165 3.34533 10.8973C3.59223 8.97816 4.46868 7.19507 5.83742 5.82733C7.20615 4.45958 8.98988 3.58442 10.9092 3.33892C12.8286 3.09342 14.7752 3.49143 16.4441 4.47063C18.113 5.44983 19.4101 6.95492 20.1321 8.75016C20.8541 10.5454 20.9603 12.5294 20.434 14.3915C20.3984 14.5034 20.3857 14.6214 20.3966 14.7383C20.4075 14.8553 20.4418 14.9689 20.4975 15.0723C20.5533 15.1757 20.6292 15.2669 20.7209 15.3403C20.8125 15.4138 20.918 15.4681 21.0311 15.4999C21.1442 15.5318 21.2625 15.5406 21.379 15.5258C21.4956 15.5109 21.6079 15.4728 21.7095 15.4137C21.811 15.3546 21.8995 15.2756 21.9699 15.1815C22.0403 15.0875 22.091 14.9802 22.119 14.8661C22.7509 12.63 22.6231 10.2475 21.7557 8.09177C20.8884 5.93605 19.3304 4.12894 17.326 2.95354C15.3215 1.77814 12.9838 1.30084 10.679 1.59642C8.37421 1.892 6.23257 2.94377 4.58959 4.58695C2.94661 6.23013 1.89511 8.37189 1.5998 10.6767C1.3045 12.9815 1.78207 15.3192 2.95772 17.3235C4.13337 19.3278 5.94067 20.8856 8.09649 21.7527C10.2523 22.6198 12.6349 22.7472 14.8709 22.1152C14.984 22.0861 15.0901 22.0347 15.1831 21.964C15.276 21.8933 15.3539 21.8047 15.4121 21.7035C15.4703 21.6022 15.5077 21.4904 15.522 21.3745C15.5364 21.2586 15.5274 21.141 15.4956 21.0287C15.4638 20.9163 15.4099 20.8114 15.337 20.7202C15.2641 20.629 15.1737 20.5532 15.0711 20.4975C14.9685 20.4417 14.8558 20.407 14.7396 20.3954C14.6234 20.3839 14.506 20.3957 14.3945 20.4301H14.391Z"
                                  fill="#FF6900"
                                />
                              </svg>
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Revisit Requests Tab */}
          {activeTab === "Revisit Requests" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Location</th>
                    <th>Session</th>
                    <th>Total Price (IQD)</th>
                    <th>Payment Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAppointments.length === 0 ? (
                    <tr><td colSpan={11} style={{ textAlign: 'center', padding: '24px', color: '#89899b' }}>No records found</td></tr>
                  ) : (
                    paginatedAppointments.map((item, index) => (
                      <tr key={index}>
                        <td>{item.raw?.booking_number || item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={composeBookingChildName(item.raw)}
                            name={item.name}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell
                            childName={composeBookingChildName(item.raw)}
                          />
                        </td>
                        <td>{item.raw?.scheduled_date ? tzDateNumeric(new Date(item.raw.scheduled_date)) : '—'}</td>
                        <td>{item.raw?.scheduled_time ? (() => { const p = item.raw.scheduled_time.split(':'); const h = parseInt(p[0]); const m = p[1]; const period = h >= 12 ? 'PM' : 'AM'; const dh = h === 0 ? 12 : h > 12 ? h - 12 : h; return `${dh}:${m} ${period}`; })() : '—'}</td>
                        <td>{item.raw?.services?.map(s => s.service_name).join(', ') || '—'}</td>
                        <td>
                          <span className={`${styles["db-status"]} ${styles[getStatusColor(item.raw?.preferred_location === 'HOME_VISIT' ? 'Home Visit' : 'On-Site')]}`}>
                            {item.raw?.preferred_location === 'HOME_VISIT' ? 'Home Visit' : 'On-Site'}
                          </span>
                        </td>
                        <td>Session {item.raw?.current_session_number || 2}</td>
                        <td>{item.raw?.total_amount ? formatWholeAmount(item.raw.total_amount) : '—'}</td>
                        <td>
                          <span className={`${styles["db-status"]} ${styles[getStatusColor("Awaiting Payment")]}`}>
                            Revisit Requested
                          </span>
                        </td>
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                              <button className={styles["apt-view-btn"]} title="View">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" clipRule="evenodd" d="M12 4.5C7.30558 4.5 3.26015 7.36745 1.5 11.5C3.26015 15.6326 7.30558 18.5 12 18.5C16.6944 18.5 20.7398 15.6326 22.5 11.5C20.7398 7.36745 16.6944 4.5 12 4.5ZM12 16C9.51472 16 7.5 13.9853 7.5 11.5C7.5 9.01472 9.51472 7 12 7C14.4853 7 16.5 9.01472 16.5 11.5C16.5 13.9853 14.4853 16 12 16ZM12 14C13.3807 14 14.5 12.8807 14.5 11.5C14.5 10.1193 13.3807 9 12 9C10.6193 9 9.5 10.1193 9.5 11.5C9.5 12.8807 10.6193 14 12 14Z" fill="#626276"/>
                                </svg>
                              </button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Rejected Tab */}
          {activeTab === "Rejected" && (
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient Name</th>
                    <th>{t("childBooking.childName", "Child Name")}</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Urgency</th>
                    <th>Location</th>
                    <th>Total Price (IQD)</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10}>
                        <Loader />
                      </td>
                    </tr>
                  ) : (
                    (hasApiData ? ordersFromApi : []).length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ textAlign: "center", padding: "24px", color: "#89899b" }}>
                          No records found
                        </td>
                      </tr>
                    ) : ordersFromApi.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.id}</td>
                        <td>
                          <BookingPatientNameCell
                            childName={item.childName}
                            name={item.patientName}
                          />
                        </td>
                        <td>
                          <BookingChildNameCell childName={item.childName} />
                        </td>
                        <td>{item.serviceDate}</td>
                        <td>{item.serviceTime}</td>
                        <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || "N/A")}</td>

                        <td>
                          {item.urgency && (
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                            >
                              {item.urgency}
                            </span>
                          )}
                        </td>

                        <td>
                          <span
                            className={`${styles["db-status"]} ${styles[getStatusColor(item.location)]}`}
                          >
                            {item.location}
                          </span>
                        </td>

                        <td>{item.totalPrice}</td>
                        <td>
                          <div className={styles["apt-action-buttons"]}>
                            <Link to={`/physio_appointment_detail?id=${item.raw?.id || ''}`}>
                            <button
                              className={styles["apt-view-btn"]}
                              title="View"
                            >
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                  fill="#155DFC"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                  fill="#155DFC"
                                />
                              </svg>
                            </button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className={styles["apt-pagination"]}>
            <div className={styles["apt-pagination-info"]}>
              {t("dashboard.pagination.showing")}{" "}
              {(currentPage - 1) * itemsPerPage + 1}{" "}
              {t("dashboard.pagination.to")}{" "}
              {(currentPage - 1) * itemsPerPage + paginatedAppointments.length}{" "}
              {t("dashboard.pagination.of")} {totalPages * itemsPerPage}
            </div>
            <div className={styles["apt-pagination-controls"]}>
              <button
                className={`${styles["apt-pagination-btn"]} ${
                  currentPage === 1 ? styles["disabled"] : ""
                }`}
                // onClick={() => handlePageChange(currentPage - 1)}
                // onClick={() => handlePageChange11(currentPage)}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {t("dashboard.pagination.previous")}
              </button>
              <div className={styles["apt-page-numbers"]}>
                {/* {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    className={`${styles["apt-page-btn"]} ${
                      styles[`${currentPage === page ? "active" : ""}`]
                    }`}
                    onClick={() => handlePageChange(currentPage)}
                  >
                    {page}
                  </button>
                );
              })} */}

                {Array.from({ length: totalPages }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`${styles["apt-page-btn"]} ${
                        styles[`${currentPage === page ? "active" : ""}`]
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              <button
                className={`${styles["apt-pagination-btn"]} ${
                  styles["apt-next-btn"]
                } ${currentPage === totalPages ? styles["disabled"] : ""}`}
                // onClick={() => handlePageChange(currentPage + 1)}
                // onClick={() => handlePageChange1(currentPage)}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                {t("dashboard.pagination.next")}
              </button>
            </div>
          </div>
        </div>{" "}
      </>

      {isChatOpen && (
        <Chat
          onClose={closeChat}
          patientUserId={chatTarget?.patientUserId}
          bookingId={chatTarget?.bookingId}
          title={chatTarget?.patientName || "Chat"}
          subtitle={chatTarget?.id}
        />
      )}

      {/* Invoice (Print) Modal — same design as the physio appointment detail invoice (NO insurance) */}
      <PhysioInvoiceModal
        open={showInvoiceModal}
        orderId={invoiceOrderId}
        onClose={() => setShowInvoiceModal(false)}
      />
    </PhysioDashboardLayout>
  );
};

export default PhysioOrderManagement;
